import { useQuery } from '@tanstack/react-query'
import { Button, Skeleton, Spin } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { ArrowLeft, Send, Sparkles } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import type { KeyboardEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  coreStepIndex,
  getOrCreateJourney,
  JourneyEntry,
  JourneyScope,
  OnboardingErrorField,
  registerProperties,
  track,
  useExitBeacon,
  useSessionReplay,
  useStepTimer
} from 'src/analytics'
import {
  integrationRpcPresetsListOptions,
  organizationsDomainMatchRetrieveOptions
} from 'src/api/main'
import { demoPresetsInfo } from 'src/features/organizations/components/IndustrySelect/demoPresetsInfo'
import { useDocumentTitle, useSessionContext } from 'src/hooks'
import { LanguageSwitcher } from 'src/layout/AppLayoutShell'
import { PublicSplitShell } from 'src/layout/PublicSplitShell'
import { paths } from 'src/router'

import {
  journeyProperties,
  validationErrorCode,
  wizardStepOf
} from './analytics'
import { NO_DATA_PRESET_CODENAME, PRIORITY_ICONS } from './constants'
import { BuildActivityPanel, useBuildActivity } from './done'
import type { PreviewData } from './preview/PreviewMock'
import { PreviewPanel } from './preview/PreviewPanel'
import { AttributionStep } from './steps/AttributionStep'
import { DetailsStep } from './steps/DetailsStep'
import { DoneScreen } from './steps/DoneScreen'
import { InviteStep } from './steps/InviteStep'
import { JoinExistingStep } from './steps/JoinExistingStep'
import { TemplateStep } from './steps/TemplateStep'
import { FORM_COLUMN_WIDTH, SOLO_COLUMN_WIDTH, useStyles } from './styles'
import type { DraftInvite } from './types'
import {
  useCreateWorkspace,
  type CreateWorkspaceOutcome
} from './useCreateWorkspace'
import { useHandleAvailability } from './useHandleAvailability'
import { useOnboardingState } from './useOnboardingState'

/**
 * Host the workspace slug becomes a subdomain of. Mirrors `workspaceOrigin` in
 * `utils/inviteUrl`: the first hostname segment is this app, the rest is shared.
 */
const resolveBaseHost = (): string => {
  const parts = window.location.hostname.split('.')

  return parts.length > 1 ? parts.slice(1).join('.') : window.location.hostname
}

/**
 * How far a step travels on its way in and out. The design system's modal lift
 * — big enough to register as movement, small enough not to read as a slide.
 */
const STEP_SHIFT = 8

/** `mid` step of the motion scale, run twice because the swap waits. */
const STEP_TRANSITION = { duration: 0.16, ease: [0.2, 0.8, 0.2, 1] as const }

/** The domain verdict does not change while somebody fills in a form. */
const DOMAIN_MATCH_STALE_MS = 5 * 60 * 1000

const greetingKey = (hour: number): string => {
  if (hour < 12) return 'workspaces.onboarding.preview.greeting.morning'
  if (hour < 18) return 'workspaces.onboarding.preview.greeting.afternoon'

  return 'workspaces.onboarding.preview.greeting.evening'
}

/**
 * Redesigned workspace onboarding — a stepped form on the left with a live,
 * skeleton-first preview of the resulting workspace on the right.
 *
 * Sits inside whichever shell the route provides (focused chrome on first run,
 * the full app shell afterwards), per the wizard layout pattern.
 *
 * The workspace is created once, when the last step is submitted. Creating it
 * earlier — as the source design does on step 1 — would leave half-configured
 * workspaces behind whenever someone leaves the flow part-way.
 */
export const OnboardingPage = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const { user, activeWorkspaceId } = useSessionContext()
  const navigate = useNavigate()
  const reduceMotion = useReducedMotion()

  /**
   * Where "back" leads, or null when there is nowhere to go — the first-ever
   * workspace is created from an account that has none, and an exit that lands
   * on nothing is worse than no exit.
   *
   * Frozen at mount: finishing the flow adds a workspace to the session, and a
   * first-run user must not grow an escape hatch out from under themselves
   * halfway through. It doubles as the "is this the first workspace?" signal.
   */
  const [returnWorkspaceId] = useState<number | null>(
    () => activeWorkspaceId ?? (user?.organizations ?? [])[0]?.id ?? null
  )
  const isFirstWorkspace = returnWorkspaceId === null

  const state = useOnboardingState()
  const { run, isRunning } = useCreateWorkspace()

  useDocumentTitle(t('workspaces.onboarding.documentTitle'))

  const [photoFiles, setPhotoFiles] = useState<UploadFile[]>([])
  const [outcome, setOutcome] = useState<CreateWorkspaceOutcome | null>(null)

  const { draft, filledInvites, focus } = state
  const photoUrl = photoFiles[0]?.url ?? photoFiles[0]?.preview
  const baseHost = useMemo(() => resolveBaseHost(), [])
  const availability = useHandleAvailability(draft.handle)

  /**
   * Whether this person's company is already here.
   *
   * Asked on every run of the wizard, not only the first: the point of BR-10 is
   * that one email domain ends up with one workspace, and the second one is
   * exactly the one worth heading off.
   */
  const domainMatch = useQuery({
    ...organizationsDomainMatchRetrieveOptions(),
    staleTime: DOMAIN_MATCH_STALE_MS
  })

  /**
   * Whether the fork was answered with "I'll make my own anyway". Read from the
   * persisted draft, so a reload remembers it and emptying the name field cannot
   * resurrect the fork.
   */
  const joinDeclined = draft.joinDeclined

  /**
   * The workspaces on this person's domain they could still ask to join.
   *
   * Ones they are already in are dropped: somebody standing in the room is not
   * asking to be let into it — they came here to add another workspace on
   * purpose, and offering them that door reads as a refusal to do what they
   * asked. If that leaves nothing, there is no fork to show.
   */
  const joinCandidates = (domainMatch.data?.organizations ?? []).filter(
    (entry) =>
      !(user?.organizations ?? []).some(
        (organization) => Number(organization.id) === entry.id
      )
  )

  /**
   * The fork is withheld once the draft has been touched. It arrives late by
   * nature, and replacing a form somebody is typing into reads as the app
   * changing its mind about what they are allowed to do.
   */
  const isDraftUntouched =
    draft.step === 'details' && !draft.name.trim() && !draft.handle.trim()

  const joinTarget =
    !joinDeclined && joinCandidates.length > 0 && isDraftUntouched
      ? joinCandidates
      : null

  /**
   * The verdict can still take the screen away, so nothing is committed to it
   * yet.
   *
   * Showing the first step immediately is right — but only when it is the step
   * the user is going to keep. While the domain answer is in flight the flow
   * does not yet know whether this person's company is already here, and
   * rendering the form anyway meant it visibly changed its mind a moment after
   * opening: the details step appeared, then the fork replaced it. One request,
   * cached for five minutes afterwards, so this is a frame or two on the first
   * visit and nothing at all on the way back.
   */
  const awaitingDomainVerdict =
    !joinDeclined && isDraftUntouched && domainMatch.isPending

  const { data: presetsData } = useQuery(integrationRpcPresetsListOptions())
  // Null for the clean workspace: `no_data` is a preset with a name of its own,
  // and printing it on the summary would claim the workspace was seeded from a
  // template when the whole point of that tile is that it was not.
  /**
   * What the summary calls the industry the workspace was seeded from.
   *
   * The catalog is asked first and the presets list only as a fallback: the
   * catalog is local, so the name is right on the very first render, whereas
   * waiting for the list meant the summary said "clean workspace" — the null
   * case — for as long as the request was in flight, about an industry the user
   * had just picked.
   */
  const presetName =
    !draft.industry || draft.industry === NO_DATA_PRESET_CODENAME
      ? null
      : draft.industry in demoPresetsInfo
        ? t(
            demoPresetsInfo[draft.industry as keyof typeof demoPresetsInfo]
              .titleKey
          )
        : ((presetsData?.results ?? []).find(
            (preset) => preset.codename === draft.industry
          )?.name ?? null)

  const firstName = user?.user?.first_name ?? ''
  const lastName = user?.user?.last_name ?? ''
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()

  const trimmedName = draft.name.trim()
  const userId = user?.user?.id
  const inviteCount = filledInvites.length

  /**
   * The step for analytics, or null when no wizard step is on screen — the Done
   * screen, and the domain fork, which is a decision about whether to enter the
   * wizard at all rather than a step inside it. Also the "is the journey still
   * open?" signal the exit beacon needs.
   */
  const wizardStep = joinTarget ? null : wizardStepOf(draft.step)
  const getTiming = useStepTimer(draft.step)

  useExitBeacon({ step: wizardStep, getTiming })

  // Recorded for as long as this page is mounted, and nowhere else in the app.
  // This is the one flow where *why* somebody stopped is the entire question —
  // and the one place worth spending the recording quota on.
  useSessionReplay()

  /**
   * Which door the user came through. A first-ever workspace is reached from
   * signup, so its journey already started on the auth screens; anything else is
   * an existing account adding a workspace, and this page is the beginning.
   */
  const entry = isFirstWorkspace ? JourneyEntry.SIGNUP : JourneyEntry.IN_APP

  /** Guards the once-per-mount report against StrictMode's double invocation. */
  const reportedEntry = useRef(false)

  useEffect(() => {
    if (reportedEntry.current) return
    reportedEntry.current = true

    const journey = getOrCreateJourney()
    registerProperties({ scope: JourneyScope.CORE, entry })

    // `started` already fired wherever the journey actually began, which for the
    // signup path is the Register screen.
    if (journey.created) track('onboarding_started', { entry })
  }, [entry])

  /**
   * Step already reported as viewed. StrictMode runs this effect twice per mount,
   * which double-counted every view and so halved the apparent completion rate of
   * every step. Keyed on the step rather than a boolean, so genuinely returning to
   * a step still reports it.
   */
  const viewedStep = useRef<string | null>(null)

  useEffect(() => {
    if (!wizardStep || viewedStep.current === wizardStep) return

    viewedStep.current = wizardStep

    // Indexed against the whole journey, not this wizard: the auth screens are
    // steps 0-2, so `state.stepIndex` (which restarts at 0 here) would collide
    // with them and make "furthest step reached" meaningless.
    track('onboarding_step_viewed', {
      step: wizardStep,
      step_index: coreStepIndex(wizardStep)
    })
  }, [wizardStep])

  /** Last reported slug verdict, so one answer is not re-sent on every render. */
  const reportedVerdict = useRef<string | null>(null)

  useEffect(() => {
    // Only the two server-settled verdicts; `short` and `checking` are states,
    // not answers.
    if (availability !== 'free' && availability !== 'taken') return

    const verdictKey = `${draft.handle}:${availability}`
    if (reportedVerdict.current === verdictKey) return

    reportedVerdict.current = verdictKey
    track('onboarding_handle_checked', { verdict: availability })
  }, [availability, draft.handle])

  /**
   * Memoised so hovering a control — which only moves the spotlight — does not
   * hand `PreviewMock` a fresh object and defeat its `memo`.
   */
  const previewData: PreviewData = useMemo(
    () => ({
      workspaceName: trimmedName || null,
      tileName: state.settledName || null,
      photoUrl,
      handle: draft.handle || null,
      workspaceUrl: draft.handle ? `${draft.handle}.${baseHost}` : null,
      primaryDomain: draft.handle || null,
      userName: fullName || null,
      userId,
      greeting: fullName
        ? t(greetingKey(new Date().getHours()), { name: firstName || fullName })
        : null,
      role: fullName ? t('workspaces.onboarding.preview.meta.owner') : null,
      members: trimmedName ? 1 + inviteCount : null,
      // How many apps an industry installs is only known once provisioning
      // runs, so this stays a skeleton rather than an invented number. A clean
      // workspace is no different: it is seeded with no demo data, not with no
      // apps, so a hard 0 here would be wrong as well as premature.
      apps: null,
      roles: null,
      // Same reasoning: the defaults are only applied while the workspace is
      // being created, and the flow never asks about sign-in methods.
      authMethods: null
    }),
    [
      trimmedName,
      state.settledName,
      photoUrl,
      draft.handle,
      baseHost,
      fullName,
      firstName,
      userId,
      inviteCount,
      t
    ]
  )

  /**
   * Commits the workspace with exactly the addresses passed in.
   *
   * Skipping sends an empty list rather than emptying `draft.invites`: the rows
   * are what the user typed, and creation can still fail and drop them back on
   * this step — clearing the draft would lose their work to a button that only
   * meant "don't send these".
   */
  const submit = async (invites: DraftInvite[]) => {
    const result = await run({ draft, invites, photoFiles })
    if (!result) {
      // Creation failed; errors are surfaced by useCreateWorkspace. Stay put so
      // the user can fix the offending field instead of landing on a Done screen
      // for a workspace that does not exist.
      state.goToStep('invite')

      return
    }
    setOutcome(result)
    state.goToStep('done')
  }

  /**
   * The slug's verdict is async and lives outside the draft, so `goNext` cannot
   * validate it — the step's action is blocked here instead. `checking` blocks
   * too: the check only starts once typing settles, so this never flickers
   * mid-keystroke, and letting it through would race the answer.
   */
  const handleBlocked =
    draft.step === 'details' &&
    (availability === 'short' ||
      availability === 'checking' ||
      availability === 'taken')

  const advance = () => {
    if (!wizardStep) return

    if (handleBlocked) {
      // `checking` is not a refusal — the answer simply has not arrived. Only a
      // verdict the user has to act on counts as friction.
      if (availability !== 'checking') {
        track('onboarding_step_error', {
          step: wizardStep,
          field: OnboardingErrorField.HANDLE,
          error_code: availability
        })
      }

      return
    }

    // Validated here as well as inside `goNext` so the offending field is known
    // before the state update hides it. Pure and cheap; see `validateStep`.
    const failure = state.validateStep()
    if (failure) {
      track('onboarding_step_error', {
        step: wizardStep,
        field: failure.field,
        error_code: validationErrorCode(failure.key)
      })
    } else {
      // Promoted on leaving the step that answered the question, so every later
      // event carries it — otherwise the first event with a company size would
      // be the one *after* the details step. The step's completion itself is
      // the platform's to record, out of the profile it is about to be sent.
      registerProperties(journeyProperties(wizardStep, draft))
    }

    if (draft.step === 'invite') {
      if (!state.goNext()) return
      void submit(filledInvites)

      return
    }
    state.goNext()
  }

  const isLastStep = draft.step === 'invite'
  const hasInvites = filledInvites.length > 0

  /**
   * Only the committing step carries an icon. The intermediate steps say
   * "Continue" on a full-width button — an arrow there restates the button.
   */
  const ctaIcon = !isLastStep ? undefined : hasInvites ? (
    <Send size={14} />
  ) : (
    <Sparkles size={14} />
  )

  const ctaLabel = !isLastStep
    ? t('continue')
    : hasInvites
      ? t('workspaces.onboarding.invite.send')
      : t('workspaces.onboarding.createWorkspace')

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return
    const target = event.target as HTMLElement
    if (target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON') return
    event.preventDefault()
    advance()
  }

  // The summary is a step of the flow, not a page of its own: it stays in the
  // same card, beside the preview it has just finished describing. It brings
  // its own actions, so the shared footer stands down.
  const done = draft.step === 'done' && outcome ? outcome : null

  const activity = useBuildActivity(done?.setupJobId, presetName === null)

  /**
   * The first step is on screen immediately.
   *
   * The domain verdict used to gate the whole flow behind a spinner, so arriving
   * from the password screen meant an empty pane before anything appeared —
   * signing in and then waiting reads as two events instead of one continuous
   * step. The verdict is only needed to *offer* the fork, and `joinTarget`
   * already waits for it: until it answers there is simply nothing to offer.
   *
   * The fork is withheld once the draft has been touched — see
   * `isDraftUntouched` above.
   */
  /**
   * The domain fork has nothing to preview and brings its own actions, so the
   * second pane and the shared footer stand down for it.
   *
   * The summary is not one of them. Its second pane is the build panel rather
   * than the mock — the workspace exists by then, and what is worth watching is
   * it being provisioned.
   */
  const isSolo = joinTarget !== null

  /**
   * The summary and the solo screens each bring their own primary action — and
   * a skeleton has nothing to continue from yet.
   */
  const ownsActions = isSolo || done !== null || awaitingDomainVerdict

  /**
   * The wizard's own steps stand down once there is a summary to show. In the
   * ordinary flow `draft.step` is already `done` by then, but the dev preview
   * forces the summary on top of whatever step the draft was left on — without
   * this the invite rows render underneath it.
   */
  const showWizardStep =
    done === null && joinTarget === null && !awaitingDomainVerdict

  /**
   * The two answers the flow asked for, reported back on the panel with the
   * icon they were picked by. The industry comes from the local catalog rather
   * than the presets request, so it is right on the first render — and the
   * clean-workspace tile is not an industry, so it contributes nothing.
   */
  const industryFacet =
    draft.industry &&
    draft.industry !== NO_DATA_PRESET_CODENAME &&
    draft.industry in demoPresetsInfo
      ? (() => {
          const info =
            demoPresetsInfo[draft.industry as keyof typeof demoPresetsInfo]

          return { label: t(info.titleKey), Icon: info.icon }
        })()
      : undefined

  const priorityFacet = draft.priority
    ? {
        label: t(`workspaces.onboarding.priority.${draft.priority}`),
        Icon: PRIORITY_ICONS[draft.priority]
      }
    : undefined

  const aside = done ? (
    <BuildActivityPanel
      workspaceName={done.organization.name}
      workspaceId={Number(done.organization.id)}
      workspaceUrl={`${done.organization.domain_name}.${baseHost}`}
      photoUrl={photoUrl}
      memberCount={1 + done.invitesSent}
      invitesSent={done.invitesSent}
      activity={activity}
      industry={industryFacet}
      priority={priorityFacet}
    />
  ) : isSolo ? null : (
    <PreviewPanel data={previewData} focus={focus} />
  )

  return (
    // The same stage the sign-in screens wear: wordmark top-left, content
    // centred, legal links bottom-left, one rounded slab down the right. The
    // flow is the step straight after signing up, so it must not have paddings
    // of its own — no language picker either, that belongs to the account by
    // the time somebody is here.
    <PublicSplitShell
      brandLinked={false}
      aside={aside}
      contentMaxWidth={isSolo ? SOLO_COLUMN_WIDTH : FORM_COLUMN_WIDTH}
      footerControls={<LanguageSwitcher />}
    >
      <div className={styles.contentColumn}>
        {/* While the workspace is being created the form is sealed: the
            requests are already in flight and there is nothing safe to change.
            The spin covers only this pane, so the shell's own header — and
            with it signing out — stays reachable. */}
        <Spin
          spinning={isRunning}
          description={
            isRunning ? t('workspaces.onboarding.creating') : undefined
          }
        >
          <div className={styles.formBody} onKeyDown={onKeyDown}>
            <div className={styles.formContent}>
              {
                <>
                  {/* Not a step-back — the flow deliberately has none. This is
                    the way out of the flow entirely, so it names where it
                    lands rather than saying just "Back", and it only offers
                    itself on the opening step, before anything is filled in. */}
                  {returnWorkspaceId !== null && draft.step === 'details' ? (
                    <Button
                      type="text"
                      size="small"
                      icon={<ArrowLeft size={14} />}
                      style={{ alignSelf: 'flex-start' }}
                      onClick={() =>
                        navigate(
                          paths.workspaces.overview(String(returnWorkspaceId))
                        )
                      }
                    >
                      {t('workspaces.onboarding.backToWorkspace')}
                    </Button>
                  ) : null}

                  {/* The summary is deliberately outside the step swapper.
                        `AnimatePresence initial={false}` suppresses the first
                        entrance of every motion element *underneath* it, not
                        just its own child — nested inside, the summary's whole
                        arrival sequence was silently cancelled while the panel
                        beside it (a sibling) played normally. It is a terminal
                        screen, not a step, so it owns its own entrance. */}
                  {done ? (
                    <div className={styles.stepPanel}>
                      <DoneScreen
                        outcome={done}
                        presetName={presetName}
                        activity={activity}
                        photoUrl={photoUrl}
                      />
                    </div>
                  ) : null}

                  {/* One step at a time, keyed so a change swaps the whole panel:
                    the outgoing one lifts and fades out before the incoming one
                    settles in, which reads as moving through a flow rather than
                    as the fields being rewritten underneath the cursor. */}
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={
                        awaitingDomainVerdict
                          ? 'awaiting'
                          : joinTarget
                            ? 'join'
                            : draft.step
                      }
                      className={styles.stepPanel}
                      initial={{
                        opacity: 0,
                        y: reduceMotion ? 0 : STEP_SHIFT
                      }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: reduceMotion ? 0 : -STEP_SHIFT }}
                      transition={STEP_TRANSITION}
                    >
                      {/* Shaped like the step it stands in for — a heading and
                          a couple of fields — so the arrival of the real one is
                          a fill-in rather than a jump. Never a spinner: the
                          shell, the wordmark and the preview stay put. */}
                      {awaitingDomainVerdict ? (
                        <Skeleton
                          active
                          title={{ width: '58%' }}
                          paragraph={{
                            rows: 3,
                            width: ['100%', '100%', '42%']
                          }}
                        />
                      ) : null}

                      {joinTarget ? (
                        <JoinExistingStep
                          entries={joinTarget}
                          onCreateInstead={() => state.declineJoin()}
                        />
                      ) : null}
                      {showWizardStep && draft.step === 'details' ? (
                        <DetailsStep
                          state={state}
                          photoFiles={photoFiles}
                          onPhotoFilesChange={setPhotoFiles}
                          baseHost={baseHost}
                          availability={availability}
                        />
                      ) : null}
                      {showWizardStep && draft.step === 'template' ? (
                        <TemplateStep state={state} />
                      ) : null}
                      {showWizardStep && draft.step === 'attribution' ? (
                        <AttributionStep state={state} />
                      ) : null}
                      {showWizardStep && draft.step === 'invite' ? (
                        <InviteStep state={state} />
                      ) : null}
                    </motion.div>
                  </AnimatePresence>
                </>
              }
            </div>

            {/* Pinned to the bottom edge of the card. Back is offered from the
                  second step on — it re-enters the previous step with the draft
                  intact, so an earlier answer (a wrong address, the wrong size)
                  can be fixed without re-typing the rest. The opening step has
                  none: its "Back to workspace" link above is the way out.

                  No Skip on the invite step: leaving the rows empty and pressing
                  the primary action already sends no invites, and the label says
                  so — it reads "Create workspace" instead of "Send invitations". */}
            {ownsActions ? null : (
              <div style={{ flex: '0 0 auto', display: 'flex', gap: 12 }}>
                {state.stepIndex > 0 ? (
                  <Button
                    size="large"
                    icon={<ArrowLeft size={14} />}
                    disabled={isRunning}
                    onClick={state.goBack}
                  >
                    {t('back')}
                  </Button>
                ) : null}
                <Button
                  type="primary"
                  size="large"
                  style={{ flex: 1 }}
                  icon={ctaIcon}
                  iconPlacement="end"
                  loading={isRunning}
                  disabled={handleBlocked}
                  onClick={advance}
                >
                  {ctaLabel}
                </Button>
              </div>
            )}
          </div>
        </Spin>
      </div>
    </PublicSplitShell>
  )
}
